import { alpha, createTheme } from "@mui/material/styles";
import { BUSINESS_THEME, IS_CLINIC } from "../platform/config";

const SPORTS_COLORS = {
  BlackText: "#151414ff",
  blackColor: "#280101ff",
  WhiteText: "#FFFFFF",
  WhiteBackground: "#FFFFFF",
  activeBackground: "#D9D9D914",
  MainColor: "#1976d2",
  SUCCESS: "#20ce60ff",
  ORANGE: "#f59e0b",
  BorderColor: "#e0e0e0",
  textFieldBackgroundColor: "#E0E0E0",
  placeHolderColor: "#868585",
  RED: "#eb0000ff",
  typographyColor: "#1C1C1C",
  SidebarActiveBg: "#e0f2ff",
  SidebarActiveText: "#2563eb",
  SidebarInactiveText: "#6b7280",
  MainBackground: "#f7f7f7",
};

/**
 * Self-serve build: the colours of the organization's business type (clinic
 * teal, spa green, salon copper, beauty rose, neutral indigo), each picked so
 * white text and accent-on-tint clear 4.5:1.
 */
const CLINIC_COLORS: typeof SPORTS_COLORS = {
  ...SPORTS_COLORS,
  MainColor: BUSINESS_THEME.main,
  SidebarActiveBg: BUSINESS_THEME.tint,
  SidebarActiveText: BUSINESS_THEME.main,
  BorderColor: BUSINESS_THEME.border,
  MainBackground: BUSINESS_THEME.background,
  typographyColor: BUSINESS_THEME.text,
};

export const COLORS = IS_CLINIC ? CLINIC_COLORS : SPORTS_COLORS;

export const CLINIC_FONT_STACK =
  '"Figtree", "Noto Sans", system-ui, -apple-system, "Segoe UI", sans-serif';

// Sports: palette, typography and shape stay on MUI's defaults so existing
// pages render exactly as before. Clinic: its own palette and type, same
// motion polish layered on top.
const baseTheme = createTheme(
  IS_CLINIC
    ? {
        palette: {
          primary: { main: BUSINESS_THEME.main },
          secondary: { main: BUSINESS_THEME.secondary },
          background: { default: BUSINESS_THEME.background, paper: "#FFFFFF" },
          text: { primary: BUSINESS_THEME.text, secondary: BUSINESS_THEME.textSecondary },
        },
        typography: { fontFamily: CLINIC_FONT_STACK },
        shape: { borderRadius: 10 },
      }
    : {}
);
const easeOut = "cubic-bezier(0.2, 0, 0, 1)";

const focusRing = `0 0 0 3px ${alpha(BUSINESS_THEME.main, 0.35)}`;

/**
 * Clinic build only: the calm, accessible look from the clinic design system.
 * Flat bordered surfaces instead of shadows, sentence-case buttons, 40px+
 * controls and a visible focus ring on everything keyboard-reachable.
 */
const CLINIC_COMPONENTS = {
  MuiButtonBase: {
    styleOverrides: {
      root: {
        "&.Mui-focusVisible": { boxShadow: focusRing },
      },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        textTransform: "none" as const,
        fontWeight: 600,
        borderRadius: 10,
        minHeight: 40,
        transition: baseTheme.transitions.create(
          ["background-color", "box-shadow", "border-color", "color", "transform"],
          { duration: baseTheme.transitions.duration.short }
        ),
        "&:active": { transform: "scale(0.97)" },
        "&.Mui-focusVisible": { boxShadow: focusRing },
      },
      sizeSmall: { minHeight: 34 },
    },
  },
  MuiPaper: {
    styleOverrides: {
      elevation1: {
        boxShadow: `0 1px 2px rgba(18, 52, 59, 0.06), 0 0 0 1px ${BUSINESS_THEME.border}`,
      },
    },
  },
  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: { border: `1px solid ${BUSINESS_THEME.border}`, borderRadius: 14 },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        backgroundColor: "#FFFFFF",
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: BUSINESS_THEME.main,
          borderWidth: 2,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontWeight: 600,
        color: BUSINESS_THEME.textSecondary,
        backgroundColor: BUSINESS_THEME.background,
        borderBottom: `1px solid ${BUSINESS_THEME.border}`,
      },
    },
  },
  MuiChip: {
    styleOverrides: { root: { fontWeight: 600 } },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: { fontSize: "0.8rem", backgroundColor: BUSINESS_THEME.text },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
        [baseTheme.breakpoints.down("sm")]: {
          margin: 12,
          width: "calc(100% - 24px)",
          maxWidth: "calc(100% - 24px) !important",
          minWidth: "0 !important",
          maxHeight: "calc(100% - 24px)",
        },
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: { root: { fontWeight: 700 } },
  },
};

export const appTheme = createTheme(baseTheme, {
  components: {
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: "3px 3px 0 0",
          transition: `all 300ms ${easeOut}`,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          minHeight: 44,
          borderRadius: "8px 8px 0 0",
          transition: baseTheme.transitions.create(
            ["color", "background-color"],
            { duration: baseTheme.transitions.duration.short }
          ),
          "&:hover": {
            color: baseTheme.palette.primary.main,
            backgroundColor: alpha(baseTheme.palette.primary.main, 0.04),
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          transition: baseTheme.transitions.create(
            ["background-color", "box-shadow", "border-color", "color", "transform"],
            { duration: baseTheme.transitions.duration.short }
          ),
          "&:active": { transform: "scale(0.97)" },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: baseTheme.transitions.create(
            ["background-color", "color", "transform"],
            { duration: baseTheme.transitions.duration.shortest }
          ),
          "&:active": { transform: "scale(0.92)" },
        },
      },
    },
    // Both builds: on a phone a dialog never runs past the screen edge, even
    // when its page gave it a fixed minWidth for desktop.
    MuiDialog: {
      styleOverrides: {
        paper: {
          [baseTheme.breakpoints.down("sm")]: {
            margin: 12,
            width: "calc(100% - 24px)",
            maxWidth: "calc(100% - 24px) !important",
            minWidth: "0 !important",
            maxHeight: "calc(100% - 24px)",
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          [baseTheme.breakpoints.down("sm")]: {
            flexWrap: "wrap",
            gap: 8,
            "& > :not(style) ~ :not(style)": { marginLeft: 0 },
          },
        },
      },
    },
    ...(IS_CLINIC ? CLINIC_COMPONENTS : {}),
  },
});
